import { useMemo, useState, type CSSProperties } from 'react';
import { useDropzone } from 'react-dropzone';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';

import { DillModal } from '../../../../../general/Dill/DillModal';
import { DillButton } from '../../../../../general/Dill/DillButton';
import { DillTable, DillColumn } from '../../../../../general/Dill/DillTable';
import { openDillToast } from '../../../../../../utils/helpers';
import { useAppDispatch } from '../../../../../../redux/store';
import { updateSupplier } from '../../../../../../redux/services/suppliersService';
import { updateSupplierBranches } from '../../../../../../redux/services/supplierAdditionalDetailsService';
import {
  updateUserSupplierBranches,
  updateUserSupplierDetails,
} from '../../../../../../redux/globalSlices/authSlice';
import {
  mapImportRow,
  mergeImportedBranches,
  SalesRegion,
  SupplierBranch,
} from '@dill/dill-shared';

const UploadIcon = () => (
  <svg
    width={22}
    height={22}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round">
    <path d="M12 16V4M12 4l-4 4M12 4l4 4M4 16v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" />
  </svg>
);

const baseDropStyle: CSSProperties = {
  padding: '28px 16px',
  borderWidth: 2,
  borderRadius: 12,
  borderColor: '#e5e7eb',
  borderStyle: 'dashed',
  outline: 'none',
  transition: 'border-color .2s ease-in-out, background-color .2s ease-in-out',
  textAlign: 'center',
};
const acceptDropStyle: CSSProperties = { borderColor: '#22c55e', backgroundColor: '#f0fdf4' };
const rejectDropStyle: CSSProperties = { borderColor: '#ef4444', backgroundColor: '#fef2f2' };

// One row as parsed from the file, plus what the merge preview decided about it.
interface PreviewRow {
  addressId: string;
  name: string;
  address1: string;
  city: string;
  state: string;
  postalCode: string;
  territoryCode: string;
  status: 'New' | 'Skipped' | 'Error';
  reason?: string;
}

const readFileAsBinaryString = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      if (event.target?.result) resolve(event.target.result.toString());
      else reject(new Error('Failed to read file'));
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsBinaryString(file);
  });

const parseFileToRawRows = async (file: File): Promise<Record<string, unknown>[]> => {
  const result = await readFileAsBinaryString(file);

  if (file.name.toLowerCase().endsWith('.csv')) {
    return new Promise((resolve, reject) => {
      Papa.parse(result, {
        header: true,
        skipEmptyLines: true,
        complete: (parsed) => resolve(parsed.data as Record<string, unknown>[]),
        error: (err: Error) => reject(err),
      });
    });
  }

  const workbook = XLSX.read(result, { type: 'binary' });
  const sheetName = workbook.SheetNames[0];
  return XLSX.utils.sheet_to_json<Record<string, unknown>>(workbook.Sheets[sheetName], {
    raw: false,
    defval: '',
  });
};

export const ImportBranchesModal = ({
  open,
  onClose,
  supplierId,
  existingBranches,
  existingRegions,
  onImported,
}: {
  open: boolean;
  onClose: () => void;
  supplierId: string;
  existingBranches: SupplierBranch[];
  existingRegions: SalesRegion[];
  onImported: (branches: SupplierBranch[], regions: SalesRegion[]) => void;
}) => {
  const dispatch = useAppDispatch();

  const [fileName, setFileName] = useState('');
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [parseError, setParseError] = useState('');
  const [previewRows, setPreviewRows] = useState<PreviewRow[]>([]);
  // The exact branches/regions arrays to send if the user confirms - computed once at
  // preview time so the summary counts and the actual write never disagree.
  const [pendingResult, setPendingResult] = useState<{
    branches: SupplierBranch[];
    regions: SalesRegion[];
    createdBranches: number;
    createdTerritories: number;
  } | null>(null);

  const reset = () => {
    setFileName('');
    setParsing(false);
    setImporting(false);
    setParseError('');
    setPreviewRows([]);
    setPendingResult(null);
  };

  const handleCloseModal = () => {
    reset();
    onClose();
  };

  const handleFile = async (file: File) => {
    setParsing(true);
    setParseError('');
    setFileName(file.name);
    try {
      const rawRows = await parseFileToRawRows(file);
      if (rawRows.length === 0) {
        setParseError('That file has no rows to import.');
        setParsing(false);
        return;
      }

      const validRows: Parameters<typeof mergeImportedBranches>[0]['rows'] = [];
      const errorRows: { row: Record<string, unknown>; error: string }[] = [];
      rawRows.forEach((rawRow) => {
        const mapped = mapImportRow(rawRow);
        if ('error' in mapped) errorRows.push({ row: rawRow, error: mapped.error });
        else validRows.push(mapped);
      });

      const merged = mergeImportedBranches({
        existingBranches,
        existingRegions,
        rows: validRows,
      });

      const skippedByAddressId = new Map(merged.skipped.map((s) => [s.addressId, s.reason]));
      const rows: PreviewRow[] = validRows.map((row) => {
        const addressId = row.addressId.trim();
        const reason = skippedByAddressId.get(addressId);
        return {
          addressId,
          name: row.name,
          address1: row.address1,
          city: row.city,
          state: row.state,
          postalCode: row.postalCode,
          territoryCode: row.territoryCode,
          status: reason ? 'Skipped' : 'New',
          reason,
        };
      });
      errorRows.forEach(({ row, error }) => {
        rows.push({
          addressId: String(row['Address ID'] ?? row.addressId ?? ''),
          name: String(row.Name ?? ''),
          address1: '',
          city: '',
          state: '',
          postalCode: '',
          territoryCode: '',
          status: 'Error',
          reason: error,
        });
      });

      setPreviewRows(rows);
      setPendingResult({
        branches: merged.branches,
        regions: merged.regions,
        createdBranches: merged.createdBranches,
        createdTerritories: merged.createdTerritories,
      });
    } catch (err) {
      console.error(err);
      setParseError('Could not read that file. Make sure it is a .csv, .xls, or .xlsx export.');
    } finally {
      setParsing(false);
    }
  };

  const onDrop = (acceptedFiles: File[]) => {
    if (acceptedFiles[0]) handleFile(acceptedFiles[0]);
  };

  const { getRootProps, getInputProps, isDragAccept, isDragReject } = useDropzone({
    onDrop,
    multiple: false,
    accept: {
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/csv': ['.csv'],
    },
  });

  const dropStyle = useMemo(
    () => ({
      ...baseDropStyle,
      ...(isDragAccept ? acceptDropStyle : {}),
      ...(isDragReject ? rejectDropStyle : {}),
    }),
    [isDragAccept, isDragReject]
  );

  const columns: DillColumn<PreviewRow>[] = [
    { key: 'addressId', label: 'Address ID', width: 110 },
    { key: 'name', label: 'Name', width: 220 },
    { key: 'address1', label: 'Address', width: 200 },
    { key: 'city', label: 'City', width: 130 },
    { key: 'state', label: 'State', width: 70 },
    { key: 'postalCode', label: 'Postal Code', width: 100 },
    { key: 'territoryCode', label: 'Territory', width: 140 },
    {
      key: 'status',
      label: 'Status',
      width: 220,
      render: (_value, row) => {
        const cls =
          row.status === 'New'
            ? 'text-green-700 bg-green-50'
            : row.status === 'Skipped'
            ? 'text-amber-700 bg-amber-50'
            : 'text-red-700 bg-red-50';
        return (
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
            {row.status}
            {row.reason ? ` — ${row.reason}` : ''}
          </span>
        );
      },
    },
  ];

  const handleImport = async () => {
    if (!pendingResult) return;
    setImporting(true);
    const result = await dispatch(
      updateSupplierBranches({
        supplierId,
        branches: pendingResult.branches,
      })
    );
    await dispatch(
      updateSupplier({
        supplierId,
        supplierDetails: { regions: pendingResult.regions },
      })
    );
    setImporting(false);

    if (result.type.includes('fulfilled')) {
      openDillToast({
        type: 'SUCCESS',
        message: `Imported ${pendingResult.createdBranches} branch${
          pendingResult.createdBranches === 1 ? '' : 'es'
        }${
          pendingResult.createdTerritories
            ? ` and created ${pendingResult.createdTerritories} territor${
                pendingResult.createdTerritories === 1 ? 'y' : 'ies'
              }`
            : ''
        }.`,
      });
      dispatch(updateUserSupplierBranches(pendingResult.branches));
      dispatch(updateUserSupplierDetails({ regions: pendingResult.regions }));
      onImported(pendingResult.branches, pendingResult.regions);
      handleCloseModal();
    } else {
      openDillToast({ type: 'ERROR', message: 'Failed to import branches.' });
    }
  };

  const skippedCount = previewRows.filter((r) => r.status === 'Skipped').length;
  const errorCount = previewRows.filter((r) => r.status === 'Error').length;

  return (
    <DillModal
      open={open}
      onClose={handleCloseModal}
      title="Import Branches"
      subtitle="Upload a CSV or Excel export of branches, with or without territories."
      maxWidth="900px"
      footer={
        pendingResult ? (
          <div className="flex items-center justify-between w-full">
            <DillButton onClick={handleCloseModal} label="Cancel" variant="secondary" />
            <DillButton
              onClick={handleImport}
              label={`Import ${pendingResult.createdBranches} branch${
                pendingResult.createdBranches === 1 ? '' : 'es'
              }`}
              isLoading={importing}
              isDisabled={pendingResult.createdBranches === 0}
            />
          </div>
        ) : (
          <div className="flex justify-end w-full">
            <DillButton onClick={handleCloseModal} label="Cancel" variant="secondary" />
          </div>
        )
      }>
      {!pendingResult ? (
        <div className="">
          <div {...getRootProps()} style={dropStyle} className="cursor-pointer">
            <input {...getInputProps()} />
            <div className="flex flex-col items-center gap-2 text-gray-500">
              <UploadIcon />
              <p className="text-sm font-medium text-gray-700 m-0">
                {parsing
                  ? 'Reading file…'
                  : 'Drop a .csv, .xls, or .xlsx file here, or click to browse'}
              </p>
              <p className="text-xs text-gray-400 m-0">
                Required columns: Address ID, Name. Territory columns are optional.
              </p>
            </div>
          </div>
          {parseError && <p className="text-xs text-red-500 mt-3">{parseError}</p>}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500 m-0">
              <span className="font-medium text-gray-700">{fileName}</span> — {previewRows.length}{' '}
              row
              {previewRows.length === 1 ? '' : 's'} found
            </p>
            <DillButton onClick={reset} label="Choose a different file" variant="ghost" />
          </div>
          <div className="flex gap-3">
            <div className="bg-green-50 border border-green-100 rounded-lg px-3 py-2 text-xs text-green-700">
              <span className="font-bold">{pendingResult.createdBranches}</span> new branch
              {pendingResult.createdBranches === 1 ? '' : 'es'}
            </div>
            <div className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 text-xs text-blue-700">
              <span className="font-bold">{pendingResult.createdTerritories}</span> new territor
              {pendingResult.createdTerritories === 1 ? 'y' : 'ies'}
            </div>
            <div className="bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 text-xs text-amber-700">
              <span className="font-bold">{skippedCount}</span> skipped (duplicate)
            </div>
            {errorCount > 0 && (
              <div className="bg-red-50 border border-red-100 rounded-lg px-3 py-2 text-xs text-red-700">
                <span className="font-bold">{errorCount}</span> row{errorCount === 1 ? '' : 's'}{' '}
                with errors
              </div>
            )}
          </div>
          <DillTable
            columns={columns}
            data={previewRows}
            rowKey={(row, index) => row.addressId || index}
            maxHeight="360px"
            striped
          />
        </div>
      )}
    </DillModal>
  );
};

export default ImportBranchesModal;
